// One booking — the confirmation right after booking, and the page a
// "My bookings" row opens. The status is re-read from the device's own
// bookings (there is no customer door to a single booking); the booking
// the book page just placed is shown at once while that read lands.

import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { BOOKING_STATUS_LABEL, dateLabel, fetchMyBookings, fetchStore, timeLabel, type Booking, type StorePublic } from '../../lib/api';

export function BookingDetail() {
  const { rid, sid, bookingId } = useParams() as { rid: string; sid: string; bookingId: string };
  const location = useLocation();
  const handed = (location.state as { booking?: Booking; placed?: boolean } | null) ?? null;
  const [booking, setBooking] = useState<Booking | null>(handed?.booking?.bookingId === bookingId ? handed.booking : null);
  const [store, setStore] = useState<StorePublic | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStore(rid, sid).then(setStore, () => {});
    fetchMyBookings(rid, sid).then(
      (mine) => {
        const b = mine.find((x) => x.bookingId === bookingId);
        if (b) setBooking(b);
        else setMissing(true);
      },
      (e) => setError(e instanceof Error ? e.message : 'Could not load the booking.'),
    );
  }, [rid, sid, bookingId]);

  const head = (
    <header className="co-head">
      <Link to={`/r/${rid}/s/${sid}/bookings`} className="co-backbtn" aria-label="Back to my bookings">‹</Link>
      <div>
        <div className="co-store">{store?.name ?? '…'}</div>
        <h1>{handed?.placed ? 'Booking requested' : 'Your booking'}</h1>
      </div>
    </header>
  );

  if (!booking) {
    return (
      <div className="co-page">
        {head}
        {error && <div className="co-err bk-err" role="alert">{error}</div>}
        {missing && <div className="co-sec"><p className="bk-lede">This booking isn't on this device.</p></div>}
        {!error && !missing && <div className="loading">Looking…</div>}
      </div>
    );
  }

  const b = booking;
  return (
    <div className="co-page">
      {head}
      <section className="co-sec" data-section="booking" data-booking={b.bookingId} data-status={b.status}>
        <span className={'bk-status bk-badge ' + b.status}>{BOOKING_STATUS_LABEL[b.status]}</span>
        <div className="bk-when">
          <span className="bk-date">{dateLabel(b.bookingDate, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          <span className="bk-time-big">{timeLabel(b.bookingTime)}</span>
        </div>
        <dl className="bk-facts">
          <dt>Guests</dt><dd data-pax={b.pax}>{b.pax}</dd>
          <dt>Name</dt><dd>{b.customerName}</dd>
          <dt>Mobile</dt><dd>{b.customerPhoneE164}</dd>
          {b.tableName && (<><dt>Table</dt><dd>{b.tableName}</dd></>)}
          {b.notes && (<><dt>Note</dt><dd>{b.notes}</dd></>)}
        </dl>
        {b.status === 'new' && <div className="co-hint">The store will confirm shortly — check back here.</div>}
        {b.status === 'rejected' && b.statusReason && <div className="bk-reason" data-reason>{b.statusReason}</div>}
        {b.status === 'cancelled' && b.statusReason && <div className="bk-reason" data-reason>{b.statusReason}</div>}
      </section>
      {store && (
        <section className="co-sec" data-section="store">
          <h2>The store</h2>
          <p className="bk-lede">{store.address}</p>
          <p className="bk-lede">To change or cancel, call <a className="linky" href={`tel:${store.phoneE164}`}>{store.phoneE164}</a>.</p>
        </section>
      )}
      <div className="bk-links">
        <Link to={`/r/${rid}/s/${sid}`} className="co-back">← Back to the menu</Link>
      </div>
    </div>
  );
}
